import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Sparkles, AlertTriangle } from 'lucide-react';

const DressCode = () => {
  const { language, t, isBangla, hasSelectedLanguage } = useLanguage();

  if (!hasSelectedLanguage) return null;

  const avoidColors = [
    { name: { en: "Red", bn: "লাল" }, hex: "#FF0000" },
    { name: { en: "White", bn: "সাদা" }, hex: "#FFFFFF" }
  ];

  return (
    <section 
      id="dress-code" 
      className="py-16 sm:py-20 md:py-28 px-4 relative overflow-hidden bg-[#FFF8F0]"
      data-testid="dress-code-section"
    >
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Chapter Indicator */}
        <motion.div 
          className="text-center mb-6 flex items-center justify-center gap-3"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          <span className={`text-[#D4AF37] text-sm tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light ${isBangla ? 'font-bangla' : ''}`}>
            {t('chapterTwo')}
          </span>
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
        </motion.div>

        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <motion.div 
            className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mb-6"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8 }}
          />
          <motion.h2 
            className={`font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-[#800020] mb-2 ${isBangla ? 'font-bangla' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="dress-code-title"
          >
            {t('dressCodeTitle')}
          </motion.h2>
          <motion.p 
            className={`text-lg sm:text-xl text-[#D4AF37] font-heading ${isBangla ? 'font-bangla' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t('dressCodeSubtitle')}
          </motion.p>
          <motion.div 
            className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mt-6"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </div>

        {/* Intro Text */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg mb-6 sm:mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <p className={`text-[#2D2424]/80 text-sm sm:text-base whitespace-pre-line leading-relaxed ${isBangla ? 'font-bangla leading-loose' : ''}`}>
            {t('dressCodeIntro')}
          </p>
        </motion.div>

        {/* Please Avoid Section */}
        <motion.div
          className="bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg mb-6 sm:mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 sm:w-6 h-5 sm:h-6 text-red-500 flex-shrink-0" />
            <h3 className={`font-heading text-xl sm:text-2xl text-red-700 font-semibold ${isBangla ? 'font-bangla' : ''}`}>
              {t('avoidTitle')}
            </h3>
          </div>
          
          {/* Avoid Colors */}
          <div className="flex gap-4 sm:gap-6 justify-center mb-4">
            {avoidColors.map((color) => (
              <div key={color.hex} className="flex flex-col items-center">
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-md border-2 border-red-200 relative"
                  style={{ backgroundColor: color.hex }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-0.5 bg-red-500 rotate-45" />
                  </div>
                </div>
                <span className={`text-sm mt-2 text-red-600 font-medium ${isBangla ? 'font-bangla' : ''}`}>
                  {color.name[language] || color.name.en}
                </span>
              </div>
            ))}
          </div>

          <p className={`text-red-700/80 text-sm sm:text-base text-center ${isBangla ? 'font-bangla leading-relaxed' : ''}`}>
            {t('avoidDescription')}
          </p>
        </motion.div>

        {/* Outdoor Note */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm border border-[#E6C288] rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <p className={`text-[#2D2424]/80 text-sm sm:text-base whitespace-pre-line leading-relaxed ${isBangla ? 'font-bangla leading-loose' : ''}`}>
            {t('outdoorNote')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default DressCode;
